import { calculateDiscountForProduct } from "./discountUtils";

/**
 * Get location-specific inventory from locationPricing array
 * @param locationPricing - Array of location pricing objects
 * @param locationId - Location ID to match
 * @returns Inventory quantity for the location, or undefined if not found
 */
const getLocationInventory = (
  locationPricing: any[] | undefined,
  locationId: string | undefined,
): number | undefined => {
  if (!locationPricing || !Array.isArray(locationPricing) || !locationId) {
    return undefined;
  }
  const locationPricingEntry = locationPricing.find(
    (lp: any) => String(lp.locationId) === String(locationId),
  );
  return locationPricingEntry?.inventoryQuantity !== undefined
    ? locationPricingEntry.inventoryQuantity
    : undefined;
};

/**
 * Calculate available inventory for a product based on selected variant options
 * @param product - Product object with variantOptions
 * @param selectedVariantOptions - Selected variant options
 * @param locationId - Optional location ID for location-specific inventory
 * @returns Available inventory quantity (0 if not set/null/undefined)
 */
export const calculateAvailableInventory = (
  product: any,
  selectedVariantOptions?:
    | Record<string, string>
    | Array<{ optionName: string; optionValue: string }>,
  locationId?: string,
): number => {
  // Convert array format to object format if needed
  let selectedOptions: Record<string, string> = {};
  if (Array.isArray(selectedVariantOptions)) {
    selectedOptions = selectedVariantOptions.reduce(
      (acc, opt) => {
        acc[opt.optionName] = opt.optionValue;
        return acc;
      },
      {} as Record<string, string>,
    );
  } else if (selectedVariantOptions) {
    selectedOptions = selectedVariantOptions;
  }

  // variantOptions system (single source of truth)
  if (product.variantOptions && product.variantOptions.length > 0) {
    // Check if any variant options are selected
    const hasSelectedOptions = Object.keys(selectedOptions).length > 0;

    if (hasSelectedOptions) {
      // First, check variantCombinations for the specific combination (highest priority)
      if (
        product.variantCombinations &&
        product.variantCombinations.length > 0
      ) {
        // Build combination string from selected options
        const combination = Object.entries(selectedOptions)
          .map(([optionName, optionValue]) => `${optionName}:${optionValue}`)
          .sort()
          .join("|");

        // Find matching variant combination
        const variantCombo = product.variantCombinations.find(
          (vc: any) => vc.combination === combination,
        );

        if (variantCombo) {
          // First, check location-specific inventory if locationId is provided
          if (locationId) {
            const locationInventory = getLocationInventory(
              variantCombo.locationPricing,
              locationId,
            );
            if (locationInventory !== undefined) {
              return locationInventory;
            }
          }
          // Fallback to base inventoryQuantity
          if (typeof variantCombo.inventoryQuantity === "number") {
            return variantCombo.inventoryQuantity;
          }
        }
      }

      // Fallback: Find the inventory quantity for the selected combination from variantOptions
      let minInventory: number | null = null;

      for (const option of product.variantOptions) {
        const selectedValue = selectedOptions[option.name];
        if (selectedValue) {
          const optionValue = option.values.find(
            (v: any) => v.value === selectedValue && v.isActive,
          );
          if (optionValue) {
            // First, check location-specific inventory if locationId is provided
            let optionInventory: number | undefined;
            if (locationId) {
              optionInventory = getLocationInventory(
                optionValue.locationPricing,
                locationId,
              );
            }
            // Fallback to base inventoryQuantity
            if (optionInventory === undefined) {
              optionInventory =
                typeof optionValue.inventoryQuantity === "number"
                  ? optionValue.inventoryQuantity
                  : 0;
            }
            // Treat undefined/null as 0
            const finalInventory =
              optionInventory !== undefined ? optionInventory : 0;
            if (minInventory === null || finalInventory < minInventory) {
              minInventory = finalInventory;
            }
          }
        }
      }

      // If inventory found for selected combination, return it
      if (minInventory !== null) {
        return minInventory;
      }
    }

    // No options selected or no inventory found for selected combination
    // Check if ANY variant combination has inventory (for product listings)
    let maxInventory = 0;

    // First, check variantCombinations array for any combination with inventory
    if (product.variantCombinations && product.variantCombinations.length > 0) {
      for (const variantCombo of product.variantCombinations) {
        let comboInventory: number;
        // Check location-specific inventory first if locationId is provided
        if (locationId) {
          const locationInventory = getLocationInventory(
            variantCombo.locationPricing,
            locationId,
          );
          comboInventory =
            locationInventory !== undefined
              ? locationInventory
              : typeof variantCombo.inventoryQuantity === "number"
                ? variantCombo.inventoryQuantity
                : 0;
        } else {
          // Fallback to base inventoryQuantity
          comboInventory =
            typeof variantCombo.inventoryQuantity === "number"
              ? variantCombo.inventoryQuantity
              : 0;
        }
        if (comboInventory > maxInventory) {
          maxInventory = comboInventory;
        }
      }
    }

    // Also check variant option values for inventory
    for (const option of product.variantOptions) {
      for (const optionValue of option.values) {
        if (optionValue.isActive) {
          let optionInventory: number;
          // Check location-specific inventory first if locationId is provided
          if (locationId) {
            const locationInventory = getLocationInventory(
              optionValue.locationPricing,
              locationId,
            );
            optionInventory =
              locationInventory !== undefined
                ? locationInventory
                : typeof optionValue.inventoryQuantity === "number"
                  ? optionValue.inventoryQuantity
                  : 0;
          } else {
            // Fallback to base inventoryQuantity
            optionInventory =
              typeof optionValue.inventoryQuantity === "number"
                ? optionValue.inventoryQuantity
                : 0;
          }
          if (optionInventory > maxInventory) {
            maxInventory = optionInventory;
          }
        }
      }
    }

    // Return the maximum inventory found across all variant combinations and options
    // This indicates the product has at least some inventory available
    return maxInventory;
  }

  // No variants/options - check variantCombinations first, then base inventory
  if (product.variantCombinations && product.variantCombinations.length > 0) {
    let maxInventory = 0;
    for (const variantCombo of product.variantCombinations) {
      let comboInventory: number;
      // Check location-specific inventory first if locationId is provided
      if (locationId) {
        const locationInventory = getLocationInventory(
          variantCombo.locationPricing,
          locationId,
        );
        comboInventory =
          locationInventory !== undefined
            ? locationInventory
            : typeof variantCombo.inventoryQuantity === "number"
              ? variantCombo.inventoryQuantity
              : 0;
      } else {
        // Fallback to base inventoryQuantity
        comboInventory =
          typeof variantCombo.inventoryQuantity === "number"
            ? variantCombo.inventoryQuantity
            : 0;
      }
      if (comboInventory > maxInventory) {
        maxInventory = comboInventory;
      }
    }
    // If any variant combination has inventory, return it; otherwise fall back to base inventory
    if (maxInventory > 0) {
      return maxInventory;
    }
  }

  // Check base product location-specific inventory if locationId is provided
  if (locationId) {
    const baseLocationInventory = getLocationInventory(
      product.locationPricing,
      locationId,
    );
    if (baseLocationInventory !== undefined) {
      return baseLocationInventory;
    }
  }

  // Fall back to base inventory (treat undefined/null as 0)
  return typeof product.inventoryQuantity === "number"
    ? product.inventoryQuantity
    : 0;
};

/**
 * Check if a product is out of stock
 * @param product - Product object
 * @param selectedVariantOptions - Selected variant options
 * @param locationId - Optional location ID for location-specific inventory
 * @returns true if product is out of stock (inventory <= 0 or undefined/null), false otherwise
 * Note: Digital products are never considered out of stock
 */
export const isOutOfStock = (
  product: any,
  selectedVariantOptions?:
    | Record<string, string>
    | Array<{ optionName: string; optionValue: string }>,
  locationId?: string,
): boolean => {
  // Digital products are never out of stock
  if (product?.isDigital === true) {
    return false;
  }

  const inventory = calculateAvailableInventory(
    product,
    selectedVariantOptions,
    locationId,
  );
  // Treat 0, null, or undefined as out of stock
  return inventory <= 0;
};

/**
 * Get inventory quantity display text
 * @param product - Product object
 * @param selectedVariantOptions - Selected variant options
 * @param locationId - Optional location ID for location-specific inventory
 * @returns Display text for inventory, or null if inventory is 0 or not set
 */
export const getInventoryDisplay = (
  product: any,
  selectedVariantOptions?:
    | Record<string, string>
    | Array<{ optionName: string; optionValue: string }>,
  locationId?: string,
): string | null => {
  const inventory = calculateAvailableInventory(
    product,
    selectedVariantOptions,
    locationId,
  );

  // Only show inventory if it's greater than 0
  if (inventory <= 0) {
    return null;
  }

  return inventory.toString();
};

/**
 * Get location-specific price from locationPricing array
 * @param locationPricing - Array of location pricing objects
 * @param locationId - Location ID to match
 * @returns Price for the location, or undefined if not found
 */
const getLocationPrice = (
  locationPricing: any[] | undefined,
  locationId: string | undefined,
): number | undefined => {
  if (!locationPricing || !Array.isArray(locationPricing) || !locationId) {
    return undefined;
  }
  const locationPricingEntry = locationPricing.find(
    (lp: any) => String(lp.locationId) === String(locationId),
  );
  return locationPricingEntry?.price !== undefined
    ? locationPricingEntry.price
    : undefined;
};

/**
 * Generate all possible variant combinations from variant options
 * @param options - Array of variant options
 * @returns Array of all possible combinations as Record<string, string>
 */
const generateAllVariantCombinations = (
  options: Array<{
    name: string;
    values: Array<{ value: string; isActive: boolean }>;
  }>,
): Array<Record<string, string>> => {
  const generateCombinations = (
    opts: typeof options,
    index: number = 0,
    currentCombo: Record<string, string> = {},
  ): Array<Record<string, string>> => {
    if (index >= opts.length) {
      return Object.keys(currentCombo).length > 0 ? [currentCombo] : [];
    }

    const option = opts[index];
    const activeValues = option.values.filter((v) => v.isActive);
    if (activeValues.length === 0) {
      return generateCombinations(opts, index + 1, currentCombo);
    }

    const combinations: Array<Record<string, string>> = [];
    for (const value of activeValues) {
      const newCombo = { ...currentCombo, [option.name]: value.value };
      combinations.push(...generateCombinations(opts, index + 1, newCombo));
    }
    return combinations;
  };

  return generateCombinations(options);
};

/**
 * Find the first available (non out of stock) variant combination
 * @param product - Product object with variantOptions and variantCombinations
 * @param locationId - Optional location ID for location-specific inventory
 * @returns The first available variant combination as Record<string, string>, or null if none found
 */
export const findFirstAvailableVariant = (
  product: any,
  locationId?: string,
): Record<string, string> | null => {
  if (!product.variantOptions || product.variantOptions.length === 0) {
    return null;
  }

  // First, check variantCombinations for the first available one
  if (product.variantCombinations && product.variantCombinations.length > 0) {
    for (const variantCombo of product.variantCombinations) {
      // Parse the combination string to get selected options
      const combinationParts = variantCombo.combination.split("|");
      const selectedOptions: Record<string, string> = {};
      for (const part of combinationParts) {
        const [optionName, optionValue] = part.split(":");
        if (optionName && optionValue) {
          selectedOptions[optionName.trim()] = optionValue.trim();
        }
      }

      // Check if this variant combination is in stock
      const inventory = calculateAvailableInventory(
        product,
        selectedOptions,
        locationId,
      );
      if (inventory > 0) {
        return selectedOptions;
      }
    }
  }

  // If no variantCombinations exist or none are in stock,
  // generate combinations from variantOptions and find the first available one
  const allCombinations = generateAllVariantCombinations(
    product.variantOptions,
  );

  // Find the first combination that's in stock
  for (const combination of allCombinations) {
    const inventory = calculateAvailableInventory(
      product,
      combination,
      locationId,
    );
    if (inventory > 0) {
      return combination;
    }
  }

  // No available variants found - return null
  return null;
};

/**
 * Calculate product price for display in product cards
 * Uses location-based pricing if locationId is provided
 * If product has variants, returns the price of the first available (non out of stock) variant combination
 * @param product - Product object with variantOptions and variantCombinations
 * @param locationId - Optional location ID for location-specific pricing
 * @returns Price to display (0 if not set)
 */
export const calculateProductCardPrice = (
  product: any,
  locationId?: string,
): number => {
  // If product has variant options, find the first available variant combination
  if (product.variantOptions && product.variantOptions.length > 0) {
    // First, check variantCombinations for the first available one
    if (product.variantCombinations && product.variantCombinations.length > 0) {
      for (const variantCombo of product.variantCombinations) {
        // Parse the combination string to get selected options
        const combinationParts = variantCombo.combination.split("|");
        const selectedOptions: Record<string, string> = {};
        for (const part of combinationParts) {
          const [optionName, optionValue] = part.split(":");
          if (optionName && optionValue) {
            selectedOptions[optionName.trim()] = optionValue.trim();
          }
        }

        // Check if this variant combination is in stock
        const inventory = calculateAvailableInventory(
          product,
          selectedOptions,
          locationId,
        );
        if (inventory > 0) {
          // This variant is available - return its price
          // Priority: location-specific price > variant combination price
          if (locationId) {
            const locationPrice = getLocationPrice(
              variantCombo.locationPricing,
              locationId,
            );
            if (locationPrice !== undefined) {
              return locationPrice;
            }
          }

          // Fallback to variant combination base price
          if (variantCombo.price !== undefined) {
            return variantCombo.price;
          }
        }
      }
    }

    // If no variantCombinations exist or none are in stock,
    // generate combinations from variantOptions and find the first available one
    const allCombinations = generateAllVariantCombinations(
      product.variantOptions,
    );

    // Find the first combination that's in stock
    for (const combination of allCombinations) {
      const inventory = calculateAvailableInventory(
        product,
        combination,
        locationId,
      );
      if (inventory > 0) {
        // This variant is available - calculate its price
        let price = product.basePrice || 0;
        for (const option of product.variantOptions) {
          const selectedValue = combination[option.name];
          if (selectedValue) {
            const optionValue = option.values.find(
              (v: any) => v.value === selectedValue && v.isActive,
            );
            if (optionValue && optionValue.addOnPrice) {
              price += optionValue.addOnPrice;
            }
          }
        }

        // Check for location-specific base price
        if (locationId) {
          const locationPrice = getLocationPrice(
            product.locationPricing,
            locationId,
          );
          if (locationPrice !== undefined) {
            // Add variant add-ons to location base price
            let finalPrice = locationPrice;
            for (const option of product.variantOptions) {
              const selectedValue = combination[option.name];
              if (selectedValue) {
                const optionValue = option.values.find(
                  (v: any) => v.value === selectedValue && v.isActive,
                );
                if (optionValue && optionValue.addOnPrice) {
                  finalPrice += optionValue.addOnPrice;
                }
              }
            }
            return finalPrice;
          }
        }

        return price;
      }
    }

    // No available variants found - fall back to first active value (current behavior)
    // Build the first combination by taking the first active value from each option
    const firstCombination: Record<string, string> = {};
    for (const option of product.variantOptions) {
      const firstActiveValue = option.values.find((v: any) => v.isActive);
      if (firstActiveValue) {
        firstCombination[option.name] = firstActiveValue.value;
      }
    }

    // Build combination string
    const combination = Object.entries(firstCombination)
      .map(([optionName, optionValue]) => `${optionName}:${optionValue}`)
      .sort()
      .join("|");

    // Check if there's a variant combination with a specific price
    if (
      combination &&
      product.variantCombinations &&
      product.variantCombinations.length > 0
    ) {
      const variantCombo = product.variantCombinations.find(
        (vc: any) => vc.combination === combination,
      );

      if (variantCombo) {
        // Priority: location-specific price > variant combination price
        if (locationId) {
          const locationPrice = getLocationPrice(
            variantCombo.locationPricing,
            locationId,
          );
          if (locationPrice !== undefined) {
            return locationPrice;
          }
        }

        // Fallback to variant combination base price
        if (variantCombo.price !== undefined) {
          return variantCombo.price;
        }
      }
    }

    // Fallback: Calculate from base price + variant option add-ons
    let price = product.basePrice || 0;
    for (const option of product.variantOptions) {
      const firstActiveValue = option.values.find((v: any) => v.isActive);
      if (firstActiveValue && firstActiveValue.addOnPrice) {
        price += firstActiveValue.addOnPrice;
      }
    }

    // Check for location-specific base price
    if (locationId) {
      const locationPrice = getLocationPrice(
        product.locationPricing,
        locationId,
      );
      if (locationPrice !== undefined) {
        // Add variant add-ons to location base price
        let finalPrice = locationPrice;
        for (const option of product.variantOptions) {
          const firstActiveValue = option.values.find((v: any) => v.isActive);
          if (firstActiveValue && firstActiveValue.addOnPrice) {
            finalPrice += firstActiveValue.addOnPrice;
          }
        }
        return finalPrice;
      }
    }

    return price;
  }

  // No variants - use base price with location-specific pricing if available
  let price = product.basePrice || 0;

  // Check for location-specific base price
  if (locationId) {
    const locationPrice = getLocationPrice(product.locationPricing, locationId);
    if (locationPrice !== undefined) {
      return locationPrice;
    }
  }

  return price;
};

/**
 * Calculate product compare at price (original price) for display in product cards
 * Uses location-based pricing if locationId is provided
 * If product has variants, returns the compare at price of the first variant combination
 * @param product - Product object with variantOptions and variantCombinations
 * @returns Compare at price to display, or undefined if not set
 */
export const calculateProductCardCompareAtPrice = (
  product: any,
): number | undefined => {
  // If product has variant options, get the first variant combination
  if (product.variantOptions && product.variantOptions.length > 0) {
    // Build the first combination by taking the first active value from each option
    const firstCombination: Record<string, string> = {};
    for (const option of product.variantOptions) {
      const firstActiveValue = option.values.find((v: any) => v.isActive);
      if (firstActiveValue) {
        firstCombination[option.name] = firstActiveValue.value;
      }
    }

    // Build combination string
    const combination = Object.entries(firstCombination)
      .map(([optionName, optionValue]) => `${optionName}:${optionValue}`)
      .sort()
      .join("|");

    // Check if there's a variant combination
    if (
      combination &&
      product.variantCombinations &&
      product.variantCombinations.length > 0
    ) {
      const variantCombo = product.variantCombinations.find(
        (vc: any) => vc.combination === combination,
      );

      if (variantCombo) {
        // Variant combinations don't typically have compareAtPrice, fall back to product level
        // But we could extend this in the future if needed
      }
    }
  }

  // Use product-level compareAtPrice
  return product.compareAtPrice;
};

/**
 * Calculate product price for display in product details
 * Uses location-based pricing if locationId is provided
 * @param product - Product object with variantOptions and variantCombinations
 * @param selectedVariantOptions - Selected variant options
 * @param locationId - Optional location ID for location-specific pricing
 * @param quantity - Quantity (for tiered discounts)
 * @returns Price to display (0 if not set)
 */
export const calculateProductPrice = (
  product: any,
  selectedVariantOptions?: { optionName: string; optionValue: string }[],
  locationId?: string,
): number => {
  // If product has variant options, find the matching variant combination
  if (
    selectedVariantOptions &&
    product.variantOptions &&
    product.variantOptions.length > 0
  ) {
    if (product.variantCombinations && product.variantCombinations.length > 0) {
      const combinationParts = selectedVariantOptions
        .map((selected) => `${selected.optionName}:${selected.optionValue}`)
        .sort()
        .join("|");

      const combination = product.variantCombinations.find(
        (combo: any) => combo.combination === combinationParts,
      );

      if (combination) {
        // Priority: location-specific price > variant combination price
        if (locationId) {
          const locationPrice = getLocationPrice(
            combination.locationPricing,
            locationId,
          );
          if (locationPrice !== undefined) {
            return locationPrice;
          }
        }

        // Fallback to variant combination price
        if (combination.price !== undefined) {
          return combination.price;
        }
      }
    }
  }

  // Fallback to product-level pricing
  if (locationId) {
    const locationPrice = getLocationPrice(product.locationPricing, locationId);
    if (locationPrice !== undefined) {
      return locationPrice;
    }
  }

  return product.basePrice || 0;
};

/**
 * Calculate product pricing for display in product cards (with discounts)
 * Returns both discounted price and original price
 */
export const calculateProductCardPricing = (
  product: any,
  locationId?: string,
  isPluginEnabled?: (pluginId: string) => boolean,
  imageIndex?: number,
): { price: number; originalPrice: number } => {
  // Get base price using existing logic
  const basePrice = calculateProductCardPrice(product, locationId);

  // Find variant options for the given image index
  let selectedVariantOptions:
    | { optionName: string; optionValue: string }[]
    | undefined;
  if (imageIndex !== undefined && product.variantCombinations) {
    const variantCombination = product.variantCombinations.find(
      (vc: any) => vc.imageIndex === imageIndex,
    );
    if (variantCombination && variantCombination.combination) {
      // Parse the combination string "Size:Small|Color:Red" into array of objects
      selectedVariantOptions = variantCombination.combination
        .split("|")
        .map((part: string) => {
          const [optionName, optionValue] = part.split(":");
          return { optionName, optionValue };
        });
    }
  }
  // Calculate discount
  const discountInfo = calculateDiscountForProduct({
    product,
    selectedVariantOptions,
    locationId,
    basePrice,
    isPluginEnabled,
  });

  return {
    price: discountInfo.discountedPrice,
    originalPrice: discountInfo.originalPrice,
  };
};

/**
 * Calculate product pricing for display in product details (with discounts)
 */
export const calculateProductDetailPricing = (
  product: any,
  selectedVariantOptions?: { optionName: string; optionValue: string }[],
  locationId?: string,
  quantity: number = 1,
  isPluginEnabled?: (pluginId: string) => boolean,
): { price: number; originalPrice: number } => {
  // Get base price using existing logic
  const basePrice = calculateProductPrice(
    product,
    selectedVariantOptions,
    locationId,
  );

  // Calculate discount
  const discountInfo = calculateDiscountForProduct({
    product,
    selectedVariantOptions,
    locationId,
    quantity,
    basePrice,
    isPluginEnabled,
  });
  return {
    price: discountInfo.discountedPrice,
    originalPrice: discountInfo.originalPrice,
  };
};
